import { useContext, useState, useEffect } from 'react';

import { Box, Button, Paper } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { useOnboardingStyles } from '@vyce/core/src/components/AppOnboarding';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';
import { ConfirmDialog } from '@vyce/core/src/components';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import {
  CompanyWorkerItem,
  FormParticipantInvite,
  Team,
  TimeAndAttendanceLocation,
} from '@vyce/core/src/types';

import { LocationAudience, RecipientScope } from './FormRecipientsModeStep';
import { FormSettingsStep } from './FormSettingsStep';
import { FormLinkedTeamsLocations } from './FormLinkedTeamsLocations';
import { FormRecipientsModeCard } from './FormRecipientsModeCard';
import { FormRecipientsSummary } from './FormRecipientsSummary';
import { triggerCompanySyncRequest } from '../../../api/mirroring';
import { useFormRouteSegment } from '../hooks/useFormRouteSegment';
import { useLocationAudience } from '../hooks/useLocationAudience';
import { BulkPhoneEntryApi } from '../../invites/types';

export const FormSelectRecipients = ({
  companyId,
  userId,
  resourceIds,
  createForm,
  updateForm,
  sendFormRecipients,
  hideRecipientsStep,
  isPhoneInputDisabled,
  handleEmailFieldKeyDown,
  handlePhoneFieldKeyDown,
  addEmailToList,
  addPhoneToList,
  deleteContact,
  emails,
  phones,
  selectedWorkers = [],
  toggleWorker,
  setIsPhoneInputDisabled,
  bulkPaste,
  bulkLimitHint,
  onSendToResourceWorkers,
  saveButtonTitle = 'Send',
  locations,
  teams = [],
  availableScopes = ['manual', 'location', 'team'],
  hideBackButton = false,
  combinedLinkedView = false,
}: {
  companyId?: string;
  userId?: string;
  locations: TimeAndAttendanceLocation[];
  teams?: Team[];
  resourceIds?: string[];
  saveButtonTitle?: string;
  availableScopes?: RecipientScope[];
  hideBackButton?: boolean;
  combinedLinkedView?: boolean;
  createForm: ({
    status,
    endDate,
    signature,
    clockInBlock,
    folderId,
  }: {
    status: string;
    endDate?: Date;
    signature?: boolean;
    clockInBlock?: boolean;
    folderId?: string | null;
  }) => Promise<string | null>;
  updateForm: ({
    formId,
    status,
    endDate,
    signature,
    clockInBlock,
    folderId,
    stayOnPage,
    silentSuccess,
  }: {
    formId: string;
    status: string;
    endDate?: Date;
    signature?: boolean;
    clockInBlock?: boolean;
    folderId?: string | null;
    stayOnPage?: boolean;
    silentSuccess?: boolean;
  }) => void;
  sendFormRecipients: (
    formId: string,
    options?: { extraParticipants?: FormParticipantInvite[]; skippedCount?: number }
  ) => void;
  hideRecipientsStep: () => void;
  isPhoneInputDisabled: boolean;
  handleEmailFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handlePhoneFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  addEmailToList: () => void;
  addPhoneToList: () => void;
  deleteContact: (contact: string) => void;
  emails: string[];
  phones: string[];
  selectedWorkers?: CompanyWorkerItem[];
  toggleWorker?: (worker: CompanyWorkerItem) => void;
  setIsPhoneInputDisabled: (isPhoneInputDisabled: boolean) => void;
  bulkPaste?: BulkPhoneEntryApi;
  bulkLimitHint?: string;
  onSendToResourceWorkers: (formId: string, resourceIds: string[]) => void;
}) => {
  const classes = useOnboardingStyles();
  const [loading, setLoading] = useState(false);
  const { id } = useFormRouteSegment('id');
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const { getValues } = useFormContext();
  const [recipientScope, setRecipientScope] = useState<RecipientScope>(
    availableScopes.includes('manual') ? 'manual' : availableScopes[0] ?? 'location'
  );
  const [selectedLocations, setSelectedLocations] = useState<TimeAndAttendanceLocation[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [savedDialogOpen, setSavedDialogOpen] = useState(false);
  const [locationAudience, setLocationAudience] = useState<LocationAudience>('everyone');
  const {
    counts: locationAudienceCounts,
    loading: locationAudienceLoading,
    resolveParticipants,
  } = useLocationAudience(
    companyId,
    // The combined Settings view has no audience picker, so it needn't fetch counts.
    combinedLinkedView ? [] : selectedLocations
  );

  // The clocked-in options send to a snapshot of people and don't link the locations
  // to the form. That only applies outside the combined Settings view, which has no picker.
  const isLocationSnapshot = !combinedLinkedView && locationAudience !== 'everyone';

  // Keep the "Block clock in" option visible as long as at least one location is
  // selected, regardless of which segment (Teams/Locations) is currently on screen —
  // it only ever applies to the chosen locations, so switching tabs shouldn't hide it.
  // The backend only blocks clock-in for forms linked to the location, so a snapshot can't use it.
  const canBlockClockIn = selectedLocations.length > 0 && !isLocationSnapshot;

  const snapshotCount =
    locationAudience === 'clocked_in_now'
      ? locationAudienceCounts.clockedInNow
      : locationAudienceCounts.clockedInToday;
  // A snapshot with nobody in it sends to no one, so it doesn't count as a recipient.
  const hasLocationRecipients =
    selectedLocations.length > 0 && (!isLocationSnapshot || (!!snapshotCount && !locationAudienceLoading));

  const hasAnyRecipients =
    emails.length > 0 ||
    phones.length > 0 ||
    selectedWorkers.length > 0 ||
    selectedTeams.length > 0 ||
    hasLocationRecipients;

  const onSendFormRecipientsClick = async () => {
    const endDate = getValues('endDate');
    const signature = getValues('signature');
    const clockInBlock = canBlockClockIn ? getValues('clockInBlock') : false;
    const folderId = getValues('folderId');
    const status = 'published';
    try {
      setLoading(true);
      // Resolved right before sending, so the list matches the moment Send is pressed.
      const snapshot =
        isLocationSnapshot && selectedLocations.length > 0
          ? await resolveParticipants(locationAudience as Exclude<LocationAudience, 'everyone'>)
          : null;
      const hasContacts = emails.length > 0 || phones.length > 0 || selectedWorkers.length > 0;
      if (snapshot && !snapshot.participants.length && !hasContacts && !selectedTeams.length) {
        showNotification({
          message: 'Nobody is clocked in at these locations any more. Pick another option to send the form.',
          options: { variant: 'error' },
        });
        return;
      }
      const formId = id || (await createForm({ status, endDate, signature, clockInBlock, folderId }));
      if (!formId) {
        return setLoading(false);
      }
      if (id) {
        await updateForm({
          formId: id,
          status,
          endDate,
          signature,
          clockInBlock,
          folderId,
          stayOnPage: combinedLinkedView,
          silentSuccess: combinedLinkedView,
        });
      }
      // Selections from every area (contacts, existing members, locations, teams) are
      // sent together — the summary chips show exactly what goes out.
      if (hasContacts || snapshot?.participants.length) {
        await sendFormRecipients(formId, {
          extraParticipants: snapshot?.participants,
          skippedCount: snapshot?.skipped,
        });
      }
      const teamIds = selectedTeams.map(team => team.uuid).filter(Boolean) as string[];
      // A snapshot has already been sent to the people above; linking the locations
      // would assign the form to every member instead.
      const locationIds = isLocationSnapshot
        ? []
        : (selectedLocations.map(location => location.uuid).filter(Boolean) as string[]);
      const idsFromResources = [...teamIds, ...locationIds];
      if (idsFromResources.length > 0) {
        await onSendToResourceWorkers(formId, idsFromResources);
        if (companyId) {
          triggerCompanySync(idsFromResources, companyId);
        }
      }
      if (combinedLinkedView) {
        setSavedDialogOpen(true);
      }
    } catch (error) {
      handleServerError(error);
    } finally {
      setLoading(false);
    }
  };

  const triggerCompanySync = async (resourceIdList: string[], companyIdParam: string) => {
    resourceIdList.forEach(resourceId => {
      triggerCompanySyncRequest({ companyId: companyIdParam, resourceId });
    });
  };

  useEffect(() => {
    if (!resourceIds?.length) {
      return;
    }
    const matchedTeams = teams.filter(t => resourceIds.includes(t.uuid));
    const matchedLocations = locations.filter(location => resourceIds.includes(location.uuid as string));

    if (combinedLinkedView) {
      // The combined view keeps both selections, so load whatever the form already
      // links to and show the segment that has linked resources.
      if (matchedTeams.length) {
        setSelectedTeams(matchedTeams);
      }
      if (matchedLocations.length) {
        setSelectedLocations(matchedLocations);
      }
      if (matchedTeams.length) {
        setRecipientScope('team');
      } else if (matchedLocations.length) {
        setRecipientScope('location');
      }
      return;
    }

    const teamSet = new Set(teams.map(t => t.uuid));
    const locSet = new Set(locations.map(l => l.uuid).filter(Boolean) as string[]);
    const allAreTeams = resourceIds.every(id => teamSet.has(id));
    const allAreLocs = resourceIds.every(id => locSet.has(id));
    if (allAreTeams && teams.length > 0) {
      setRecipientScope('team');
      setSelectedTeams(matchedTeams);
    } else if (allAreLocs && locations.length > 0) {
      setRecipientScope('location');
      setSelectedLocations(matchedLocations);
    }
  }, [resourceIds, teams, locations, combinedLinkedView]);

  return (
    <Box className={classes.mainContainer}>
      <Box className={classes.paper}>
        <Box
          className={classes.root}
          style={{
            overflow: 'hidden',
            position: 'relative',
            gap: 24,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {combinedLinkedView ? (
            <FormLinkedTeamsLocations
              teams={teams}
              locations={locations}
              selectedTeams={selectedTeams}
              setSelectedTeams={setSelectedTeams}
              selectedLocations={selectedLocations}
              setSelectedLocations={setSelectedLocations}
              recipientScope={recipientScope}
              setRecipientScope={setRecipientScope}
            />
          ) : (
            <FormRecipientsModeCard
              availableScopes={availableScopes}
              recipientScope={recipientScope}
              setRecipientScope={setRecipientScope}
              companyId={companyId}
              selectedWorkers={selectedWorkers}
              toggleWorker={toggleWorker}
              teams={teams}
              locations={locations}
              selectedTeams={selectedTeams}
              setSelectedTeams={setSelectedTeams}
              selectedLocations={selectedLocations}
              setSelectedLocations={setSelectedLocations}
              isPhoneInputDisabled={isPhoneInputDisabled}
              handleEmailFieldKeyDown={handleEmailFieldKeyDown}
              handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
              addEmailToList={addEmailToList}
              addPhoneToList={addPhoneToList}
              deleteContact={deleteContact}
              emails={emails}
              phones={phones}
              setIsPhoneInputDisabled={setIsPhoneInputDisabled}
              bulkPaste={bulkPaste}
              bulkLimitHint={bulkLimitHint}
              locationAudience={locationAudience}
              setLocationAudience={setLocationAudience}
              locationAudienceCounts={locationAudienceCounts}
              locationAudienceLoading={locationAudienceLoading}
            />
          )}

          {hasAnyRecipients && (
            <Paper variant="outlined" style={{ padding: 24 }}>
              <FormRecipientsSummary
                selectedWorkers={selectedWorkers}
                onRemoveWorker={toggleWorker}
                selectedTeams={selectedTeams}
                onRemoveTeam={team =>
                  setSelectedTeams(selectedTeams.filter(item => (item.uuid ?? item.name) !== (team.uuid ?? team.name)))
                }
                selectedLocations={selectedLocations}
                locationAudience={isLocationSnapshot ? locationAudience : 'everyone'}
                onRemoveLocation={location =>
                  setSelectedLocations(
                    selectedLocations.filter(
                      item => (item.uuid ?? item.name) !== (location.uuid ?? location.name)
                    )
                  )
                }
                emails={emails}
                phones={phones}
                onRemoveContact={deleteContact}
              />
            </Paper>
          )}

          {/* <FormEndDateStep /> */}

          <FormSettingsStep companyId={companyId} userId={userId} showClockInBlock={canBlockClockIn} />

          <Paper variant="outlined" style={{ padding: 24 }}>
            <Box display="flex" gridGap={16} justifyContent={hideBackButton ? 'flex-end' : 'space-between'}>
              {!hideBackButton && (
                <Button
                  variant="outlined"
                  style={{ width: 100 }}
                  size="large"
                  color="primary"
                  onClick={hideRecipientsStep}
                >
                  Back
                </Button>
              )}

              <Button
                style={{ width: 150 }}
                variant="contained"
                size="large"
                color="primary"
                onClick={onSendFormRecipientsClick}
                disabled={loading || (!combinedLinkedView && !hasAnyRecipients)}
              >
                <ButtonTitleWithLoading title={saveButtonTitle} loaderVariant="primary" loading={loading} />
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <ConfirmDialog
        open={savedDialogOpen}
        handleClose={() => setSavedDialogOpen(false)}
        handleConfirm={() => setSavedDialogOpen(false)}
        showCancelText={false}
        title="Changes saved"
        subtitle="Your changes have been saved. Just to note, anyone who was already invited to this form hasn't been removed. They'll still have access. If you'd like to remove them, you can do this in the Recipients tab above."
        confirmText="Got it"
      />
    </Box>
  );
};
